import React from "react";
import { Badge } from "@/components/ui/badge";
import { Monitor, Smartphone, Globe } from "lucide-react";
import { DeviceIcon } from "@/components/DeviceIcon";
import DashboardWidget from "@/components/DashboardWidget";

interface AnalyticsStats {
  deviceStats: {
    types: Record<string, number>;
    os: Record<string, number>;
    browsers: Record<string, number>;
  };
}

interface SystemInfoWidgetProps {
  stats: AnalyticsStats | null;
}

export const SystemInfoWidget: React.FC<SystemInfoWidgetProps> = ({
  stats,
}) => {
  const getTotalDevices = () => {
    return Object.values(stats?.deviceStats.types || {}).reduce(
      (sum, count) => sum + count,
      0,
    );
  };

  const getTotalOS = () => {
    return Object.values(stats?.deviceStats.os || {}).reduce(
      (sum, count) => sum + count,
      0,
    );
  };

  const getTotalBrowsers = () => {
    return Object.values(stats?.deviceStats.browsers || {}).reduce(
      (sum, count) => sum + count,
      0,
    );
  };

  const getDeviceIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "mobile":
        return <Smartphone className="h-4 w-4" />;
      case "tablet":
        return <Smartphone className="h-4 w-4" />;
      case "desktop":
        return <Monitor className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  return (
    <DashboardWidget
      id="system-info"
      title="System Analytics"
      headerActions={<Monitor className="h-5 w-5" />}
    >
      <div className="space-y-6">
        {/* Device Types */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Monitor className="h-4 w-4" />
              Device Types
            </h4>
            <Badge variant="outline" className="text-xs">
              {getTotalDevices()} total
            </Badge>
          </div>
          <div className="space-y-2">
            {Object.entries(stats?.deviceStats.types || {})
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => {
                const percentage =
                  getTotalDevices() > 0
                    ? ((count / getTotalDevices()) * 100).toFixed(1)
                    : "0";
                return (
                  <div
                    key={type}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      {getDeviceIcon(type)}
                      <span className="capitalize text-sm font-medium">
                        {type}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {percentage}%
                      </span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Operating Systems */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Operating Systems
            </h4>
            <Badge variant="outline" className="text-xs">
              {getTotalOS()} total
            </Badge>
          </div>
          <div className="space-y-2">
            {Object.entries(stats?.deviceStats.os || {})
              .sort(([, a], [, b]) => b - a)
              .slice(0, 6)
              .map(([os, count]) => {
                const percentage =
                  getTotalOS() > 0
                    ? ((count / getTotalOS()) * 100).toFixed(1)
                    : "0";
                return (
                  <div
                    key={os}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <DeviceIcon
                        os={os}
                        className="h-4 w-4"
                        showLabel={false}
                      />
                      <span className="text-sm font-medium">{os}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {percentage}%
                      </span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Browsers */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Browsers
            </h4>
            <Badge variant="outline" className="text-xs">
              {getTotalBrowsers()} total
            </Badge>
          </div>
          <div className="space-y-2">
            {Object.entries(stats?.deviceStats.browsers || {})
              .sort(([, a], [, b]) => b - a)
              .slice(0, 5)
              .map(([browser, count]) => {
                const percentage =
                  getTotalBrowsers() > 0
                    ? ((count / getTotalBrowsers()) * 100).toFixed(1)
                    : "0";
                return (
                  <div
                    key={browser}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="h-3 w-3 text-gray-500" />
                      <span className="text-sm font-medium">{browser}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {percentage}%
                      </span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </DashboardWidget>
  );
};

export default SystemInfoWidget;
